/**
 * Batches static geometry into one mesh per material.
 *
 * The neighbourhood is built from a great many small parts — every fence post,
 * every window frame, every tree lobe. That reads beautifully and draws
 * terribly: a performance pass measured 1,593 draw calls and 2,833 geometries
 * for 39,000 triangles, which is the classic shape of a scene that is
 * draw-call bound rather than fill bound.
 *
 * Anything that never moves goes through here instead. Geometries are baked
 * into world space and merged per material, so a hundred trees become two
 * draw calls rather than three hundred.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

interface Batch {
  material: THREE.Material;
  geometries: THREE.BufferGeometry[];
  castShadow: boolean;
  receiveShadow: boolean;
}

export class MeshBatcher {
  private batches = new Map<THREE.Material, Batch>();
  private readonly matrix = new THREE.Matrix4();

  /**
   * Add a piece of static geometry.
   *
   * @param geometry cloned and transformed, so callers may reuse the source
   * @param transform position, rotation and scale in world space
   */
  add(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    transform: {
      position?: THREE.Vector3 | [number, number, number];
      rotation?: THREE.Euler | [number, number, number];
      scale?: THREE.Vector3 | [number, number, number] | number;
    } = {},
    shadows: { cast?: boolean; receive?: boolean } = {},
  ): void {
    const position = toVector(transform.position, 0);
    const scale = toVector(transform.scale, 1);
    const rotation = transform.rotation instanceof THREE.Euler
      ? transform.rotation
      : new THREE.Euler(...(transform.rotation ?? [0, 0, 0]));

    this.matrix.compose(
      position,
      new THREE.Quaternion().setFromEuler(rotation),
      scale,
    );

    const baked = normalise(geometry.clone().applyMatrix4(this.matrix));

    let batch = this.batches.get(material);
    if (!batch) {
      batch = {
        material,
        geometries: [],
        castShadow: shadows.cast ?? true,
        receiveShadow: shadows.receive ?? true,
      };
      this.batches.set(material, batch);
    }
    batch.geometries.push(baked);
    if (shadows.cast) batch.castShadow = true;
    if (shadows.receive) batch.receiveShadow = true;
  }

  /** Add an existing object tree, baking its world transforms. */
  addObject(object: THREE.Object3D, shadows: { cast?: boolean; receive?: boolean } = {}): void {
    object.updateWorldMatrix(true, true);
    object.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) return;
      const material = Array.isArray(mesh.material) ? mesh.material[0]! : mesh.material;
      const baked = normalise(mesh.geometry.clone().applyMatrix4(mesh.matrixWorld));
      let batch = this.batches.get(material);
      if (!batch) {
        batch = {
          material, geometries: [],
          castShadow: shadows.cast ?? mesh.castShadow,
          receiveShadow: shadows.receive ?? mesh.receiveShadow,
        };
        this.batches.set(material, batch);
      }
      batch.geometries.push(baked);
    });
  }

  /** Emit one mesh per material. Safe to call once; the batcher is spent after. */
  build(): THREE.Group {
    const group = new THREE.Group();
    for (const batch of this.batches.values()) {
      if (batch.geometries.length === 0) continue;
      // mergeGeometries needs every input to carry the same attributes. Only
      // the batch that actually uses vertex colours pays for them: if any
      // geometry in this batch has a tint, the rest get plain white.
      if (batch.geometries.some((g) => g.attributes.color)) {
        for (const geometry of batch.geometries) {
          if (geometry.attributes.color) continue;
          const count = geometry.attributes.position!.count;
          const white = new Float32Array(count * 3).fill(1);
          geometry.setAttribute('color', new THREE.BufferAttribute(white, 3));
        }
      }
      const merged = batch.geometries.length === 1
        ? batch.geometries[0]!
        : mergeGeometries(batch.geometries, false);
      if (!merged) {
        // Silently dropping a batch means part of the world simply vanishes,
        // which is far worse than the draw calls it was meant to save.
        console.error(
          '[MeshBatcher] merge failed; falling back to individual meshes',
          batch.geometries.length,
        );
        for (const geometry of batch.geometries) {
          const mesh = new THREE.Mesh(geometry, batch.material);
          mesh.castShadow = batch.castShadow;
          mesh.receiveShadow = batch.receiveShadow;
          group.add(mesh);
        }
        continue;
      }
      const mesh = new THREE.Mesh(merged, batch.material);
      mesh.castShadow = batch.castShadow;
      mesh.receiveShadow = batch.receiveShadow;
      group.add(mesh);
      // The source geometries are no longer referenced by anything.
      if (batch.geometries.length > 1) {
        for (const g of batch.geometries) g.dispose();
      }
    }
    this.batches.clear();
    return group;
  }
}

/**
 * Put a geometry into a form that can always be merged with any other.
 *
 * `mergeGeometries` requires every input to agree on both its attribute set and
 * whether it is indexed. Three's primitives disagree on both - BoxGeometry is
 * indexed, ShapeGeometry is not - and a mismatch makes the merge return null,
 * which silently deleted whole batches of the world.
 */
function normalise(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = geometry.index ? geometry.toNonIndexed() : geometry;
  if (flat !== geometry) geometry.dispose();
  for (const name of Object.keys(flat.attributes)) {
    // `color` survives: a material with vertexColors and no colour attribute
    // reads (0, 0, 0) and renders black, with nothing to say so. That is
    // exactly what happened to the first foliage build - every canopy in the
    // neighbourhood came out a dark mass because this loop had quietly thrown
    // the tint away.
    if (name !== 'position' && name !== 'normal' && name !== 'uv' && name !== 'color') {
      flat.deleteAttribute(name);
    }
  }
  const count = flat.attributes.position!.count;
  if (!flat.attributes.normal) {
    flat.computeVertexNormals();
  }
  if (!flat.attributes.uv) {
    flat.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(count * 2), 2));
  }
  return flat;
}

function toVector(
  value: THREE.Vector3 | [number, number, number] | number | undefined,
  fallback: number,
): THREE.Vector3 {
  if (value === undefined) return new THREE.Vector3(fallback, fallback, fallback);
  if (typeof value === 'number') return new THREE.Vector3(value, value, value);
  if (Array.isArray(value)) return new THREE.Vector3(...value);
  return value.clone();
}
